// src/quiz/gre-mock6/verbalData.js
// GRE Mock 6 — Verbal Reasoning (hard set, same level as Mock 5).
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const SE_INSTRUCTION = "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.";

const GUILD_PASSAGE =
  "Economic historians long portrayed medieval craft guilds as cartels that restricted entry, fixed prices, and stifled innovation for the benefit of their members. More recently, some scholars have argued that guilds performed valuable functions: by certifying the quality of their members' work, they gave buyers in distant markets reason to trust goods they could not inspect, and by regulating apprenticeship, they ensured that skills were passed on from one generation to the next.\n\n" +
  "Critics of this revisionist view concede that guilds may have served such purposes but question whether guilds were necessary to serve them. In several regions where guilds were weak or absent, they note, merchants developed other means of guaranteeing quality, such as reputation networks and municipal inspection, and apprenticeship flourished under private contracts. If similar benefits could be obtained without the guilds' restrictions on entry and competition, then the revisionists have shown only that guilds did some good, not that they were the best available means of doing it.";

const LANGUAGE_PASSAGE =
  "It is often assumed that languages with no written form change more rapidly than written ones, since writing seems to anchor pronunciation and vocabulary. The evidence is less clear than the assumption. Icelandic, written continuously for some eight centuries, has changed remarkably little, but English, written for even longer, has changed enormously. Such cases suggest that the rate of linguistic change depends less on writing itself than on factors such as a community's isolation and its contact with speakers of other languages.";

const SERIAL_PASSAGE =
  "Many nineteenth-century novels, including most of Dickens's, first appeared in monthly or weekly installments. Literary critics of the early twentieth century tended to treat this mode of publication as a commercial constraint that compromised artistic unity: the need to end each installment on a note of suspense, they argued, encouraged sprawling plots, melodramatic coincidences, and characters introduced merely to fill space.\n\n" +
  "More recent scholarship has questioned this judgment. Serial publication, these scholars note, allowed authors to respond to readers' reactions as a novel unfolded, and some authors used that opportunity deliberately, revising their plans for later installments in light of letters and sales. Far from being merely a constraint, the installment form also fostered distinctive artistic effects: the long intervals between parts gave recurring characters a familiarity like that of acquaintances, and the shared experience of waiting for the next number made reading a communal event. To judge serial novels solely by standards derived from novels conceived as single volumes is, on this view, to misunderstand what kind of works they were.";

const CALORIE_PASSAGE =
  "Fernville has passed a law requiring every restaurant in the town to print the calorie content of each dish on its menu. Town officials predict that, as a result, the average number of calories Fernville residents consume will decline.";

const PAIN_PASSAGE =
  "In a clinical study, patients given a new pain medication reported, on average, about the same reduction in pain as patients given a placebo. Among the study's patients with chronic pain, however, those given the medication reported a significantly greater reduction in pain than those given the placebo.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "The scientist's ______ was such that she refused to publish any finding until it had been replicated at least three times.",
    options: ["temerity", "circumspection", "prodigality", "insouciance", "credulity"],
    answer: "circumspection",
    explanation: "Refusing to publish until a result is replicated three times shows great caution: circumspection. Temerity (rashness) and insouciance (carelessness) are the opposite.",
  },
  {
    id: 2,
    type: "blanks",
    question: "The novel's ending, which some readers found (i)______, is in fact carefully (ii)______ by subtle hints scattered throughout the earlier chapters.",
    blanks: [
      { options: ["inevitable", "arbitrary", "sentimental"] },
      { options: ["undermined", "obscured", "foreshadowed"] },
    ],
    answer: ["arbitrary", "foreshadowed"],
    explanation: "\"In fact carefully\" contrasts with the readers' view, so they thought the ending was arbitrary (unprepared, random). Hints in earlier chapters foreshadow it. \"Inevitable\" would agree with the second half instead of contrasting with it.",
  },
  {
    id: 3,
    type: "blanks",
    question: "In an age that prized (i)______, the essayist's willingness to (ii)______ his earlier opinions struck many as a sign of weakness; yet later readers came to regard this very (iii)______ as his greatest strength.",
    blanks: [
      { options: ["novelty", "consistency", "modesty"] },
      { options: ["reiterate", "embellish", "recant"] },
      { options: ["stubborn conviction", "intellectual flexibility", "rhetorical flourish"] },
    ],
    answer: ["consistency", "recant", "intellectual flexibility"],
    explanation: "Giving up (recanting) earlier opinions looks weak only to an age that prizes consistency. \"This very\" refers back to that willingness to change his mind: intellectual flexibility.",
  },
  {
    id: 4,
    type: "blanks",
    question: "The city's attempt to (i)______ its traffic problems by widening roads only (ii)______ them, as the new lanes quickly filled with additional cars.",
    blanks: [
      { options: ["exacerbate", "ignore", "ameliorate"] },
      { options: ["alleviated", "aggravated", "concealed"] },
    ],
    answer: ["ameliorate", "aggravated"],
    explanation: "A city widens roads to improve (ameliorate) traffic. \"Only\" and the lanes filling with more cars show the result was the opposite: the problems were aggravated.",
  },
  {
    id: 5,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Though the speaker's arguments were ______, his delivery was so engaging that few in the audience noticed.",
    options: ["cogent", "specious", "eloquent", "fallacious", "compelling", "abstruse"],
    answer: ["specious", "fallacious"],
    explanation: "What the audience failed to notice must be a flaw: the arguments were specious (plausible but wrong) or fallacious. Cogent and compelling form a pair, but there would be nothing for the audience to miss.",
  },
  {
    id: 6,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The company's ______ response to the crisis—issuing a statement only after weeks of silence—damaged its reputation more than the crisis itself.",
    options: ["prompt", "measured", "dilatory", "candid", "tardy", "defiant"],
    answer: ["dilatory", "tardy"],
    explanation: "A statement \"only after weeks of silence\" is late: dilatory and tardy. Measured suggests careful restraint, a positive word that doesn't fit the damage done.",
  },
  {
    id: 7,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The critic was known for her ______ reviews, which could end an author's career in a single paragraph.",
    options: ["laudatory", "equivocal", "scathing", "perfunctory", "withering", "effusive"],
    answer: ["scathing", "withering"],
    explanation: "Reviews that can end a career are savagely critical: scathing and withering. Laudatory and effusive (full of praise) are the opposite.",
  },
  {
    id: 8,
    passage: GUILD_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "argue that medieval guilds stifled innovation",
      "describe how apprenticeship worked in medieval Europe",
      "present a debate about how to assess the value of a historical institution",
      "show that reputation networks were superior to guilds",
      "explain why guilds disappeared in some regions",
    ],
    answer: "present a debate about how to assess the value of a historical institution",
    explanation: "The passage moves from the traditional view, to the revisionist defense, to critics' reply about whether guilds were necessary. It reports the debate without the author taking a side.",
  },
  {
    id: 9,
    type: "multi",
    passage: GUILD_PASSAGE,
    question: "According to the passage, the revisionist scholars claim that guilds did which of the following?",
    options: [
      "Helped buyers trust goods they could not examine directly",
      "Encouraged innovation more than earlier historians recognized",
      "Contributed to the passing on of skills between generations",
    ],
    answer: [
      "Helped buyers trust goods they could not examine directly",
      "Contributed to the passing on of skills between generations",
    ],
    explanation: "Revisionists say guilds gave buyers \"reason to trust goods they could not inspect\" and that apprenticeship rules ensured \"skills were passed on.\" The passage never says revisionists credit guilds with innovation.",
  },
  {
    id: 10,
    passage: GUILD_PASSAGE,
    question: "The critics' argument in the second paragraph would be most seriously weakened if it were shown that",
    options: [
      "guilds in some cities set prices higher than competitive markets would have",
      "apprenticeship contracts were common in regions with strong guilds",
      "municipal inspection was introduced in several regions during the period",
      "goods from regions where guilds were weak or absent were consistently of lower quality than goods from regions with strong guilds",
      "some revisionist scholars had earlier held the traditional view of guilds",
    ],
    answer: "goods from regions where guilds were weak or absent were consistently of lower quality than goods from regions with strong guilds",
    explanation: "The critics' case rests on the alternatives delivering \"similar benefits.\" If regions without guilds produced worse goods, the alternatives were not as good, and guilds may have been the best available means after all.",
  },
  {
    id: 11,
    passage: LANGUAGE_PASSAGE,
    question: "The author mentions Icelandic and English primarily in order to",
    options: [
      "argue that unwritten languages change more slowly than written ones",
      "illustrate the difficulty of dating linguistic changes",
      "contrast the vocabularies of two European languages",
      "show that a long written tradition does not by itself ensure a slow rate of change",
      "suggest that English would have changed less if it had been written earlier",
    ],
    answer: "show that a long written tradition does not by itself ensure a slow rate of change",
    explanation: "Both languages have long written histories, yet one changed little and the other a great deal. So writing alone cannot explain the rate of change, which is the author's point.",
  },
  {
    id: 12,
    passage: LANGUAGE_PASSAGE,
    question: "Which of the following, if true, would most support the suggestion made in the passage's last sentence?",
    options: [
      "English has a larger vocabulary than Icelandic.",
      "Icelandic speakers had relatively little contact with speakers of other languages during the period, whereas English speakers had extensive contact with speakers of Norse and French.",
      "Some unwritten languages have changed very slowly.",
      "Icelandic spelling has been reformed several times.",
      "Linguists disagree about how to measure the rate of linguistic change.",
    ],
    answer: "Icelandic speakers had relatively little contact with speakers of other languages during the period, whereas English speakers had extensive contact with speakers of Norse and French.",
    explanation: "The last sentence credits isolation and language contact. This option shows the slow-changing language was isolated and the fast-changing one had heavy contact, which is exactly what the suggestion predicts.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "The mayor's critics found her ______ maddening: she would answer any question at great length while never quite committing herself to a position.",
    options: ["bluntness", "parsimony", "evasiveness", "zealotry", "reticence"],
    answer: "evasiveness",
    explanation: "Talking at length without committing to a position is evasiveness. Reticence (reluctance to speak) fails because she talks \"at great length.\"",
  },
  {
    id: 14,
    question: "Hardly a(n) ______, the new manager immediately set about changing procedures that had gone unquestioned for decades.",
    options: ["iconoclast", "innovator", "reformer", "traditionalist", "maverick"],
    answer: "traditionalist",
    explanation: "\"Hardly a(n)\" means she is the opposite of the blank. Someone who changes long-unquestioned procedures is the opposite of a traditionalist. The other four words describe what she is.",
  },
  {
    id: 15,
    type: "blanks",
    question: "Although the drug proved (i)______ in laboratory trials, its effects in patients were so (ii)______ that regulators declined to approve it.",
    blanks: [
      { options: ["toxic", "promising", "inert"] },
      { options: ["pronounced", "predictable", "negligible"] },
    ],
    answer: ["promising", "negligible"],
    explanation: "\"Although\" contrasts the lab with the patients, and regulators rejected the drug. So it looked promising in the lab but its effects in patients were negligible (too small to matter).",
  },
  {
    id: 16,
    type: "blanks",
    question: "The once-(i)______ neighborhood, long avoided by developers, has been so thoroughly (ii)______ that many of its longtime residents can no longer (iii)______ the rents.",
    blanks: [
      { options: ["fashionable", "neglected", "exclusive"] },
      { options: ["depopulated", "preserved", "gentrified"] },
      { options: ["afford", "dispute", "recall"] },
    ],
    answer: ["neglected", "gentrified", "afford"],
    explanation: "A neighborhood \"long avoided by developers\" was neglected. If longtime residents are being priced out, it has been gentrified, and they can no longer afford the rents.",
  },
  {
    id: 17,
    type: "blanks",
    question: "The historian's account is (i)______ rather than (ii)______: it tells us in great detail what happened but rarely ventures to explain why.",
    blanks: [
      { options: ["polemical", "descriptive", "speculative"] },
      { options: ["anecdotal", "chronological", "analytical"] },
    ],
    answer: ["descriptive", "analytical"],
    explanation: "Telling \"what happened\" in detail is descriptive; explaining \"why\" is analytical. The colon clause matches the two blanks in order.",
  },
  {
    id: 18,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The ______ tone of the official report contrasted sharply with the alarm expressed by the scientists who had contributed to it.",
    options: ["alarmist", "sanguine", "strident", "technical", "untroubled", "tentative"],
    answer: ["sanguine", "untroubled"],
    explanation: "The report's tone contrasts with alarm, so it was calm and unworried: sanguine and untroubled. Alarmist and strident would match the scientists instead.",
  },
  {
    id: 19,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The new regulations are so ______ that even experienced lawyers disagree about what they require.",
    options: ["stringent", "ambiguous", "explicit", "lenient", "equivocal", "recent"],
    answer: ["ambiguous", "equivocal"],
    explanation: "If experts cannot agree on what the rules require, the rules are unclear: ambiguous and equivocal. Stringent and lenient describe strictness, not clarity.",
  },
  {
    id: 20,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Once regarded as ______, the theory is now taught in introductory courses as established fact.",
    options: ["canonical", "axiomatic", "heterodox", "influential", "unorthodox", "elegant"],
    answer: ["heterodox", "unorthodox"],
    explanation: "\"Once\" contrasts the theory's past status with its present one as established fact, so it was once outside accepted opinion: heterodox and unorthodox. Canonical and axiomatic describe its status now.",
  },
  {
    id: 21,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The actor's ______ in interviews—he rarely says more than a few words—has only increased public curiosity about his private life.",
    options: ["garrulity", "arrogance", "taciturnity", "volubility", "reticence", "hostility"],
    answer: ["taciturnity", "reticence"],
    explanation: "Rarely saying more than a few words is taciturnity or reticence. Garrulity and volubility (talkativeness) are the opposite pair.",
  },
  {
    id: 22,
    passage: SERIAL_PASSAGE,
    question: "Which of the following best expresses the main idea of the passage?",
    options: [
      "Dickens was the most skillful serial novelist of the nineteenth century.",
      "Serial publication encouraged sprawling plots and melodramatic coincidences.",
      "Recent scholarship challenges an earlier view that serial publication was simply a limitation on nineteenth-century novelists.",
      "Nineteenth-century readers preferred serial novels to novels published as single volumes.",
      "Authors of serial novels were more concerned with sales than with artistic quality.",
    ],
    answer: "Recent scholarship challenges an earlier view that serial publication was simply a limitation on nineteenth-century novelists.",
    explanation: "The first paragraph gives the early critics' view that serialization was a constraint; the second presents recent scholars' rejection of it (\"Far from being merely a constraint\"). B states only the older view.",
  },
  {
    id: 23,
    passage: SERIAL_PASSAGE,
    question: "According to the passage, early-twentieth-century critics believed that serial publication",
    options: [
      "made reading a communal experience",
      "allowed authors to respond to their readers",
      "was less profitable than publication in single volumes",
      "encouraged features that weakened the unity of novels",
      "was used mainly by novelists other than Dickens",
    ],
    answer: "encouraged features that weakened the unity of novels",
    explanation: "Those critics saw serialization as a constraint that \"compromised artistic unity\" by encouraging sprawling plots and filler characters. A and B are what recent scholars say.",
  },
  {
    id: 24,
    type: "multi",
    passage: SERIAL_PASSAGE,
    question: "The passage indicates that recent scholars regard which of the following as results of serial publication?",
    options: [
      "Novels became shorter and more tightly plotted.",
      "Readers came to regard recurring characters with a kind of familiarity.",
      "Authors could adjust later parts of a novel in response to readers.",
    ],
    answer: [
      "Readers came to regard recurring characters with a kind of familiarity.",
      "Authors could adjust later parts of a novel in response to readers.",
    ],
    explanation: "Recent scholars mention characters gaining \"a familiarity like that of acquaintances\" and authors \"revising their plans for later installments.\" Nobody in the passage says serial novels became shorter or tighter; the early critics said the opposite.",
  },
  {
    id: 25,
    passage: SERIAL_PASSAGE,
    question: "The last sentence of the passage suggests that the recent scholars would most likely agree that",
    options: [
      "serial novels are artistically superior to novels published in single volumes",
      "the standards used to evaluate a novel should take into account the way it was first published",
      "early-twentieth-century critics did not read serial novels in their original installments",
      "the commercial pressures on nineteenth-century novelists have been exaggerated",
      "novels should no longer be published in installments",
    ],
    answer: "the standards used to evaluate a novel should take into account the way it was first published",
    explanation: "Judging serial novels \"solely by standards derived from novels conceived as single volumes\" is called a misunderstanding, so the scholars think evaluation should fit the form of publication. A goes further than the passage: different, not better.",
  },
  {
    id: 26,
    passage: CALORIE_PASSAGE,
    question: "The officials' prediction depends on which of the following assumptions?",
    options: [
      "Restaurants in Fernville will not raise their prices after the law takes effect.",
      "Most Fernville residents already know the calorie content of the dishes they order.",
      "A significant number of Fernville residents who eat at restaurants will use the calorie information to choose lower-calorie dishes, without making up for it in other meals.",
      "Restaurants in neighboring towns are not required to print calorie information.",
      "Calorie content is the most important factor in a healthy diet.",
    ],
    answer: "A significant number of Fernville residents who eat at restaurants will use the calorie information to choose lower-calorie dishes, without making up for it in other meals.",
    explanation: "Printing numbers lowers consumption only if people act on them and don't eat more elsewhere. Negate C (residents ignore the information, or compensate later) and the prediction collapses. B would actually make the law pointless.",
  },
  {
    id: 27,
    passage: PAIN_PASSAGE,
    question: "Which of the following is most strongly supported by the information above?",
    options: [
      "The medication should be prescribed only to patients with chronic pain.",
      "Most of the patients in the study had chronic pain.",
      "Placebos are ineffective for patients with chronic pain.",
      "Among the study's patients who did not have chronic pain, the medication did not outperform the placebo.",
      "The medication has more side effects than the placebo.",
    ],
    answer: "Among the study's patients who did not have chronic pain, the medication did not outperform the placebo.",
    explanation: "Overall the two groups did about equally well, yet the medication did much better among chronic-pain patients. For the totals to balance, it must have done no better (in fact worse) among the other patients. A is a recommendation the data alone cannot justify.",
  },
];

export default verbalQuestions;
